<template>
  <div class="view-personal-data">
    <page-header
      title="Dados pessoais"
      subtitle="Atualize seus dados de cadastro na Blitzpay."
    />
    <page-container>
      <div class="text-center my-9" v-if="loadingInfo">
        <b-spinner variant="primary" />
      </div>

      <template v-else>
        <h3 class="mb-3">
          Perfil pessoal
        </h3>

        <div
          v-if="!isAllowEdit"
          class="p-3 border rounded border-information-400"
        >
          <div class="d-flex gap-3">
            <bp-icon icon="fa fa-info-circle" class="text-information-500" />
            <span class="functional-1 text-information-700">
              Para editar essas informações, entre em contato com o suporte
            </span>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <select-person-type
              v-if="form.name"
              v-model="form.type_account"
              @input="form.document = ''"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-6" />

          <div class="col-lg-6">
            <bp-input
              v-model="form.name"
              id="name"
              required
              label="Seu nome ou razão social da empresa"
              :errors="errors.name"
              :disabled="!isAllowEdit"
            />
          </div>

          <div class="col-lg-6">
            <bp-input
              v-model="form.email"
              type="email"
              label="E-mail"
              disabled
            />
          </div>

          <template v-if="form.type_account == 'PF'">
            <div class="col-lg-6">
              <bp-input
                v-model="form.document"
                required
                mask="###.###.###-##"
                label="Digite seu CPF"
                placeholder="ex: 123.456.789-02"
                :errors="errors.document"
                @blur="onChangeCPF"
                :disabled="!isAllowEdit"
              />
            </div>
            <div class="col-lg-6">
              <bp-input
                v-model="form.birth_date"
                masked
                required
                mask="##/##/####"
                placeholder="DD/MM/YYYY"
                label="Data de nascimento"
                :errors="errors.birth_date"
                :disabled="!isAllowEdit"
              />
            </div>
          </template>

          <template v-else>
            <div class="col-lg-6">
              <bp-input
                v-model="form.document"
                required
                label="Digite seu CNPJ"
                mask="##.###.###/####-##"
                placeholder="ex: 12.456.789/0001-32"
                :errors="errors.document"
                @blur="onChangeCNPJ"
                :disabled="!isAllowEdit"
              />
            </div>
          </template>

          <div
            v-if="user.company.token"
            class="col-lg-6 d-flex align-items-end gap-3"
          >
            <bp-input label="Token" disabled v-model="user.company.token" />
            <bp-button icon="fa-light fa-copy" @click="copyToken" />
          </div>
        </div>

        <hr class="my-6" />

        <h3>Endereço completo</h3>

        <div class="row">
          <div class="col-lg-4">
            <bp-select
              v-model="form.country"
              required
              label="País de residência"
              :options="countryOptions"
              @input="onChangeCountry"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-input
              v-model="form.zipcode"
              required
              label="CEP"
              mask="#####-###"
              placeholder="ex: 0000-000"
              :delay="500"
              @change="getZipcode(form.zipcode)"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-select
              v-if="form.country == 30"
              v-model="form.state"
              required
              label="Estado"
              :options="stateOptions"
              :disabled="loadZipcodeInfo || !isAllowEdit"
              @input="onChangeState"
            />
          </div>

          <div class="col-lg-4">
            <bp-input
              v-model="form.address"
              required
              label="Endereço"
              placeholder="ex: Rua 15 de Novembro"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-input
              v-model="form.number"
              number
              required
              label="Número"
              placeholder="ex: 123"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-input
              v-model="form.complement"
              label="Complemento"
              placeholder="ex: Casa 10, bloco 2"
              :disabled="!isAllowEdit"
            />
          </div>

          <div class="col-lg-6">
            <bp-select
              v-if="form.country == 30"
              v-model="form.city"
              required
              label="Cidade/Município"
              placeholder="ex: São Paulo"
              :options="cityOptions"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
            <bp-input
              v-else
              v-model="form.city"
              required
              label="Cidade/Município"
              placeholder="ex: São Paulo"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
          <div class="col-lg-6">
            <bp-input
              v-model="form.neighborhood"
              required
              label="Bairro"
              placeholder="ex: Jardins"
              :disabled="loadZipcodeInfo || !isAllowEdit"
            />
          </div>
        </div>

        <hr class="my-6" />

        <h3>Telefones</h3>

        <div class="row">
          <div class="col-6 col-lg-1">
            <bp-input
              v-model="currentDialCode"
              disabled
              label="País"
              placeholder="ex: +55"
            />
          </div>
          <div class="col-6 col-lg-1">
            <bp-input
              v-model="form.phone.ddd"
              required
              mask="##"
              label="DDD"
              placeholder="ex: 11"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-input
              v-model="form.phone.number"
              required
              label="Telefone"
              mask="# ####-####"
              placeholder="ex: 9 9999-9999"
              :disabled="!isAllowEdit"
            />
          </div>

          <div class="col-6 col-lg-1">
            <bp-input
              v-model="currentDialCode"
              disabled
              label="País"
              placeholder="ex: +55"
            />
          </div>
          <div class="col-6 col-lg-1">
            <bp-input
              v-model="form.phone_01.ddd"
              mask="##"
              label="DDD"
              placeholder="ex: 11"
              :disabled="!isAllowEdit"
            />
          </div>
          <div class="col-lg-4">
            <bp-input
              v-model="form.phone_01.number"
              label="Telefone"
              mask="# ####-####"
              placeholder="ex: 9 9999-9999"
              :disabled="!isAllowEdit"
            />
          </div>
        </div>

        <div class="d-lg-flex justify-content-between mt-9">
          <bp-button
            size="xl"
            class="d-none d-lg-block"
            variant="filled-1"
            label="Mudar senha"
            @click="showChangePassword = true"
          />

          <div class="d-flex flex-column flex-lg-row gap-3">
            <bp-button
              size="xl"
              label="Salvar"
              class="order-lg-1"
              :loading="loading"
              :disabled="!isValid || !isAllowEdit"
              @click="submit"
            />
            <bp-button
              size="xl"
              class="d-lg-none"
              variant="filled-1"
              label="Mudar senha"
              @click="showChangePassword = true"
            />
            <bp-button
              size="xl"
              color="neutral"
              label="Cancelar"
              variant="filled-1"
              @click="loadInfo"
            />
          </div>
        </div>
      </template>

      <responsive-modal
        v-model="showChangePassword"
        title="Mudar senha"
        description="Troque sua senha de acesso"
      >
        <change-password @close="showChangePassword = false" />
      </responsive-modal>
    </page-container>
  </div>
</template>
<script>
import ChangePassword from "@/components/user/PersonalData/ChangePassword.vue";
import SelectPersonType from "@/components/user/PersonalData/SelectPersonType";

import { COUNTRIES_LIST } from "@/constants/countries.js";
import { STATES_CITIES_LIST } from "@/constants/statesCities.js";
import { formFields, isValidCNPJ, isValidCPF } from "@/functions/helpers";

import axios from "axios";
import moment from "moment";
import { mapState } from "vuex";

export default {
  components: {
    SelectPersonType,
    ChangePassword
  },
  data() {
    return {
      ...formFields(
        [
          "type_account",
          "name",
          "email",
          "document",
          "birth_date",
          "country",
          "zipcode",
          "state",
          "address",
          "number",
          "complement",
          "city",
          "neighborhood",
          "phone",
          "phone_01"
        ],
        {
          phone: {
            country: "",
            ddd: "",
            number: ""
          },
          phone_01: {
            country: "",
            ddd: "",
            number: ""
          }
        }
      ),
      loadingInfo: false,
      loadZipcodeInfo: false,
      showChangePassword: false,
      isAllowEdit: true
    };
  },
  computed: {
    ...mapState("user", ["user"]),
    countryOptions() {
      return COUNTRIES_LIST.map((item, index) => {
        return { id: index + 1, name: item.label, dialCode: item.dialCode };
      });
    },
    stateOptions() {
      return STATES_CITIES_LIST.map((item, index) => {
        return {
          id: index + 1,
          name: item.nome,
          uf: item.sigla,
          cities: item.cidades
        };
      });
    },
    cityOptions() {
      return (
        this.form.state &&
        this.stateOptions
          .filter(item => item.id == this.form.state)[0]
          .cities.map((item, index) => {
            return { id: index + 1, name: item };
          })
      );
    },
    currentDialCode() {
      return "+55";
    },
    isValid() {
      return (
        this.form.type_account &&
        this.form.name &&
        this.form.email &&
        this.form.document &&
        (this.form.type_account == "PJ" ||
          (this.form.birth_date && this.form.birth_date.length == 10)) &&
        (this.form.type_account == "PJ"
          ? isValidCNPJ(this.form.document)
          : isValidCPF(this.form.document)) &&
        this.form.country &&
        this.form.zipcode &&
        (this.form.state || this.form.country != 30) &&
        this.form.address &&
        this.form.number &&
        this.form.city &&
        this.form.neighborhood &&
        this.form.phone.ddd &&
        this.form.phone.ddd.length == 2 &&
        this.form.phone.number
      );
    }
  },
  methods: {
    copyToken() {
      navigator.clipboard.writeText(this.user.company.token);
      this.$message.success("Token copiado");
    },
    getZipcode(val) {
      if (this.form.country == 30 && val.length == 8) {
        this.loadZipcodeInfo = true;
        const instanceAx = axios.create({ headers: {} });
        instanceAx.defaults.headers = null;
        instanceAx
          .get(`https://viacep.com.br/ws/${val}/json/`)
          .then(({ data }) => {
            this.form.state = this.stateOptions.find(
              item => item.uf == data.uf
            ).id;
            setTimeout(() => {
              this.form.city = this.cityOptions.find(
                item => item.name.toLowerCase() == data.localidade.toLowerCase()
              ).id;
            }, 1);
            this.form.neighborhood = data.bairro;
            this.form.address = data.logradouro;
          })
          .finally(() => (this.loadZipcodeInfo = false));
      }
    },
    onChangeCountry() {
      this.form.state = "";
      this.form.city = "";
    },
    onChangeState() {
      this.form.city = "";
    },
    onChangeCPF(v) {
      if (!isValidCPF(v)) this.errors.document = ["CPF inválido"];
    },
    onChangeCNPJ(v) {
      if (!isValidCNPJ(v)) this.errors.document = ["CNPJ inválido"];
    },

    submit() {
      this.loading = true;
      const data = {
        ...this.form
      };

      if (this.form.type_account == "PJ") delete data.birth_date;

      data.country = this.countryOptions.find(
        item => item.id == this.form.country
      ).name;

      data.state = this.stateOptions.find(
        item => item.id == this.form.state
      )?.uf;

      if (typeof this.form.city == "number") {
        data.city = this.cityOptions.find(
          item => item.id == this.form.city
        ).name;
      }

      data.phone =
        this.currentDialCode + this.form.phone.ddd + this.form.phone.number;
      data.phone_01 =
        this.currentDialCode +
        this.form.phone_01.ddd +
        this.form.phone_01.number;

      data.phone_02 = "";

      this.$store
        .dispatch("user/update", data)
        .then(() => {
          this.$message.success("Salvo com sucesso");
        })
        .catch(error => {
          this.errors = error.response.data;
          this.$message.error("Erro ao salvar");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadInfo() {
      this.loadingInfo = true;
      this.$store
        .dispatch("user/getInfo")
        .then(data => {
          this.form = {
            ...this.form,
            ...data,
            type_account: data.type_account || "PF"
          };

          this.isAllowEdit = data.is_allow_edit;

          if (data.birth_date)
            this.form.birth_date = moment(data.birth_date).format("DD/MM/YYYY");

          this.form.country = data.country
            ? this.countryOptions.find(opt => opt.name == data.country)?.id
            : 30;

          this.form.state =
            this.form.country == 30 && data.state
              ? this.stateOptions.find(opt => opt.uf == data.state)?.id
              : "";

          this.form.city =
            this.form.country == 30 && this.form.state && data.city
              ? this.cityOptions.find(opt => opt.name == data.city)?.id
              : data.city;

          if (data.phone) {
            this.form.phone =
              data.phone[0] == "+"
                ? {
                    ddd: data.phone.substring(3, 5),
                    number: data.phone.substring(5)
                  }
                : {
                    ddd: data.phone.substring(2, 4),
                    number: data.phone.substring(4)
                  };
          } else {
            this.form.phone = {
              ddd: "",
              number: ""
            };
          }

          if (data.phone_01) {
            this.form.phone_01 =
              data.phone_01[0] == "+"
                ? {
                    ddd: data.phone_01.substring(3, 5),
                    number: data.phone_01.substring(5)
                  }
                : {
                    ddd: data.phone_01.substring(2, 4),
                    number: data.phone_01.substring(4)
                  };
          } else {
            this.form.phone_01 = {
              ddd: "",
              number: ""
            };
          }
        })
        .finally(() => {
          this.loadingInfo = false;
        });
    }
  },
  watch: {
    "form.confirmPass": function(val) {
      if (val !== this.form.newPass) {
        this.errors.confirmPass = ["As senhas são diferentes"];
      } else {
        this.errors.confirmPass = [];
      }
    }
  },
  mounted() {
    this.loadInfo();
  }
};
</script>
<style scoped lang="scss">
.view-personal-data {
  .col-lg-6,
  .col-lg-4,
  .col-lg-1 {
    margin-top: 16px;
  }
  .row {
    align-items: flex-start;
  }
}
</style>
